import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import { ToastProvider, ToastBridge } from './components/Toast';

/**
 * Chrome no celular, tradutor e extensões mexem no texto da página.
 * O React tenta tirar um pedaço que já não está ali e a tela vira "OPS! ALGO FALHOU".
 * Se o nó não é filho, a operação já ia falhar — seguimos sem derrubar o app.
 */
function tolerateExternalDomEdits(): void {
  if (typeof Node !== 'function') return;
  const rawRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) return child;
    return rawRemoveChild.call(this, child) as T;
  };
  const rawInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, child: Node | null): T {
    if (child && child.parentNode !== this) return node;
    return rawInsertBefore.call(this, node, child) as T;
  };
}

tolerateExternalDomEdits();

// ============================================
// Sentry — Monitoramento de erros e performance
// ============================================
// Inicializa apenas se DSN configurado e enabled=true.
// Em desenvolvimento sem .env, pula silenciosamente sem quebrar o app.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true';

if (sentryEnabled && sentryDsn && !sentryDsn.includes('SUA_DSN_AQUI')) {
  Sentry.init({
    dsn: sentryDsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || 'development',
    // Performance Monitoring — 10% em prod, 100% em dev
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Session Replay — só grava quando há erro
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
  });
}

// ============================================================
// Após deploy: chunks com hash novo; pedido antigo pode falhar (HTML em vez de JS).
// Um reload traz index.html atual — evita tela "OPS" ao trocar de perfil.
// ============================================================
function isChunkOrModuleLoadError(reason: unknown): boolean {
  if (reason == null) return false;
  if (typeof reason === 'string') {
    return /ChunkLoadError|dynamically imported module|importing a module script failed|mime type|Failed to fetch|Load failed|error loading/i.test(
      reason
    );
  }
  if (typeof reason === 'object' && reason !== null && 'name' in reason) {
    const n = String((reason as { name?: string }).name || '');
    if (n === 'ChunkLoadError') return true;
  }
  const msg = typeof reason === 'object' && reason !== null && 'message' in reason
    ? String((reason as { message?: string }).message || '')
    : String(reason);
  return /ChunkLoadError|dynamically imported module|importing a module script failed|mime type|Failed to fetch|Load failed|error loading dynamically imported module/i.test(
    msg
  );
}

async function hardReloadForStaleChunk(): Promise<void> {
  try {
    // Uma tentativa automática por aba — evita loop se o chunk continuar 404.
    if (sessionStorage.getItem('eamb_chunk_reload')) return;
    sessionStorage.setItem('eamb_chunk_reload', '1');
  } catch {
    /* proceed */
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((k) => k.startsWith('eambulante-') || k.includes('workbox'))
          .map((k) => caches.delete(k))
      );
    }
  } catch {
    /* ignore */
  }
  const url = new URL(window.location.href);
  url.searchParams.set('_eamb_r', String(Date.now()));
  window.location.replace(url.toString());
}

window.addEventListener('unhandledrejection', (event) => {
  if (!isChunkOrModuleLoadError(event.reason)) return;
  event.preventDefault();
  void hardReloadForStaleChunk();
});

// Safari/iOS também dispara error em script tag (não só promise rejection)
window.addEventListener('error', (event) => {
  const msg = event.message || '';
  const filename = (event as ErrorEvent).filename || '';
  if (/chunk|assets\/.*\.js|dynamically imported module/i.test(msg + filename)) {
    void hardReloadForStaleChunk();
  }
});

// ============================================================
// 🔔 Registrar Service Workers
// ============================================================
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {

    // 1. SW principal — cache e notificações locais
    try {
      const mainReg = await navigator.serviceWorker.register('/service-worker.js', {
        updateViaCache: 'none',
      });

      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }

      setInterval(() => mainReg.update().catch(() => {}), 60 * 60 * 1000);
    } catch {
      // Ignora silenciosamente
    }

    // 2. SW do Firebase Messaging — push notifications
    try {
      const FIREBASE_CONFIG = {
        apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId:             import.meta.env.VITE_FIREBASE_APP_ID,
      };

      const fcmReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });

      const cache = await caches.open('__e_amb_fb_cfg__');
      await cache.put('firebase-config', new Response(JSON.stringify(FIREBASE_CONFIG)));

      // ✅ FIX: usar fcmReg.active em vez de "sw" (variável que não existia)
      const activeSw = fcmReg.active || fcmReg.installing || fcmReg.waiting;
      if (activeSw) {
        activeSw.postMessage({ type: 'FIREBASE_CONFIG', config: FIREBASE_CONFIG });
      }
    } catch {
      // FCM SW opcional — não interrompe o carregamento
    }

    // 3. Mensagens do SW
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'FIREBASE_CONFIG_ACK') {
        // debug interno
      }
    });
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ToastProvider>
      <ToastBridge />
      <App />
    </ToastProvider>
  </React.StrictMode>
);
